import clsx from 'clsx'
import React from 'react'

import { SolidCircleCheckIcon } from './icons/SolidCircleCheck'

export interface StepNavProps extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode;
}

interface BaseStepProps {
  /**
   * Identifies the step the user is currently on.
   */
  active?: boolean;
  /**
   * Identifies that a step is available, meaning it can be clicked and
   * navigated to. Note that the `active` step will not be clickable even if
   * it is also marked as `available`.
   */
  available?: boolean;
  /**
   * Identifies a step that has been completed. It may also be the active step.
   */
  complete?: boolean;
}

interface StepProps extends BaseStepProps, React.HTMLAttributes<HTMLLIElement> {
  children?: React.ReactNode;
  name: string | React.ReactNode;
  hideLine?: boolean;
}

interface SubStepProps extends BaseStepProps, React.HTMLAttributes<HTMLLIElement> {
  name: string
}

interface StepLinkProps extends BaseStepProps {
  children: React.ReactNode;
  hasSubSteps?: boolean;
}

interface AsLinkProps {
  /**
   * Steps render an anchor tag by default. You can use the `as` prop to
   * optionally render a button or a custom link component instead.
   */
  as?: 'a';
  href?: string;
  target?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  rel?: string;
}

interface AsButtonProps {
  /**
   * Steps can optionally render a button. An anchor tag is a better semantic
   * fit if the current step is derived from the URL, but if the current step
   * is derived purely from state and has no corresponding URL, a button may
   * be more appropriate.
   */
  as: 'button';
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

interface AsCustomLinkProps {
  /**
   * Steps can optionally render a custom link component. This may be useful
   * for components like a router link that override the default anchor tag
   * behavior to perform client-side navigation.
   */
  as: React.ComponentType<{
    children: React.ReactNode;
    className?: string;
    onClick?: React.MouseEventHandler;
    to?: string;
  }>;
  onClick?: React.MouseEventHandler<unknown>;
  to?: string;
}

interface DotProps {
  filled?: boolean;
}

function excludePolymorphicProps<T extends Record<string, unknown>> (props: T) {
  const {
    as: _as,
    href: _href,
    onClick: _onClick,
    rel: _rel,
    target: _target,
    to: _to,
    ...rest
  } = props

  return rest
}

function Dot ({ filled }: DotProps) {
  return (
    <svg
      aria-hidden
      width='12'
      height='12'
      viewBox='0 0 12 12'
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? 'none' : 'currentColor'}
      xmlns='http://www.w3.org/2000/svg'
    >
      <circle cx='6' cy='6' r='4' />
    </svg>
  )
}

function DashedDot ({ filled }: DotProps) {
  if (filled) {
    return (
      <svg
        width='12'
        height='12'
        viewBox='0 0 12 12'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <circle
          cx='6'
          cy='6'
          r='4'
          stroke='currentColor'
          strokeLinecap='round'
          strokeDasharray='2 2'
        />
        <circle cx='6' cy='6' r='2' fill='currentColor' />
      </svg>
    )
  }

  return (
    <svg
      width='12'
      height='12'
      viewBox='0 0 12 12'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <circle
        cx='6'
        cy='6'
        r='4'
        stroke='currentColor'
        strokeLinecap='round'
        strokeDasharray='2 2'
      />
    </svg>
  )
}

function StepLink (props: StepLinkProps & (AsLinkProps | AsButtonProps | AsCustomLinkProps)) {
  const { active, children, complete, available, hasSubSteps } = props

  const baseClassName = clsx(
    'flex items-center h-4 block pb-1 outline-none',
    {
      'text-gray-800': active || available || complete,
      'text-gray-500': !available,
      'body-sm-bold': active,
      'body-sm': !active,
    }
  )
  const linkClassName = clsx(
    baseClassName,
    'group/link hover:text-gray-800 hover:font-medium cursor-pointer'
  )

  function getScreenReaderStatus () {
    if (active) return 'Current step: '
    if (complete) return 'Completed step: '
    return null
  }

  function renderContent () {
    return (
      <>
        <div className='w-3 h-3'>{renderIcon()}</div>
        <div className='pl-3'>
          <span className='sr-only'>{getScreenReaderStatus()}</span>
          <span className='outline outline-offset-2 outline-2 outline-transparent group-focus-visible/link:outline-thatch-500 rounded-md'>
            {children}
          </span>
        </div>
      </>
    )
  }

  function renderIcon () {
    if (complete) {
      return (
        <span className='text-green-400'>
          <SolidCircleCheckIcon aria-hidden size={12} />
        </span>
      )
    }

    if (active) {
      return (
        <span className='text-gray-700'>
          <Dot filled />
        </span>
      )
    }

    if (available && !hasSubSteps) {
      return (
        <>
          <span className='text-yellow-400 group-has-[:hover]/link:hidden'>
            <DashedDot />
          </span>
          <span className='text-yellow-400 hidden group-has-[:hover]/link:inline'>
            <DashedDot filled />
          </span>
        </>
      )
    }

    return (
      <span className='text-gray-500'>
        <Dot />
      </span>
    )
  }

  if (hasSubSteps || active || !available) {
    return <div className={baseClassName}>{renderContent()}</div>
  }

  switch (props.as) {
    case 'a':
    case undefined:
      return (
        <a
          href={props.href}
          className={linkClassName}
          onClick={props.onClick}
          rel={props.rel}
          target={props.target}
        >
          {renderContent()}
        </a>
      )
    case 'button':
      return (
        <button className={linkClassName} onClick={props.onClick} type='button'>
          {renderContent()}
        </button>
      )
    default: {
      const Component = props.as

      return (
        <Component
          to={props.to}
          className={linkClassName}
          onClick={props.onClick}
        >
          {renderContent()}
        </Component>
      )
    }
  }
}

function Step (props: StepProps & AsLinkProps): React.ReactElement
function Step (props: StepProps & AsButtonProps): React.ReactElement
function Step (props: StepProps & AsCustomLinkProps): React.ReactElement
function Step (props: StepProps & (AsLinkProps | AsButtonProps | AsCustomLinkProps)): React.ReactElement {
  const { active, children, complete, available = false, name, hideLine = false, ...rest } = props

  function getLinkProps () {
    switch (rest.as) {
      case 'a':
      case undefined:
        return {
          as: rest.as,
          href: rest.href,
          target: rest.target,
          onClick: rest.onClick,
          rel: rest.rel,
        }
      case 'button':
        return { as: rest.as, type: 'button', onClick: rest.onClick }
      default:
        return { as: rest.as, onClick: rest.onClick, to: rest.to }
    }
  }

  return (
    <li
      {...excludePolymorphicProps(rest)}
      aria-current={!!active}
      className='pb-1 last:pb-0 group'
    >
      <StepLink
        {...getLinkProps()}
        active={active}
        available={available}
        complete={complete}
        hasSubSteps={!!children}
      >
        {name}
      </StepLink>
      <div className='flex items-stretch'>
        <div
          className={clsx('w-3 min-h-3 flex justify-center', {
            'group-last:hidden': hideLine || !children,
          })}
        >
          <div className='w-px h-full bg-gray-200 -translate-x-[0.5px]' />
        </div>
        {!!children && (
          <ul aria-label='Sub-steps' className='pl-3 mt-3'>
            {children}
          </ul>
        )}
      </div>
    </li>
  )
}

function SubStep (props: SubStepProps & AsLinkProps): React.ReactElement
function SubStep (props: SubStepProps & AsButtonProps): React.ReactElement
function SubStep (props: SubStepProps & AsCustomLinkProps): React.ReactElement
function SubStep (props: SubStepProps & (AsLinkProps | AsButtonProps | AsCustomLinkProps)): React.ReactElement {
  const { active, available = false, complete, name, ...rest } = props

  function getLinkProps () {
    switch (rest.as) {
      case 'a':
      case undefined:
        return {
          as: rest.as,
          href: rest.href,
          target: rest.target,
          onClick: rest.onClick,
          rel: rest.rel,
        }
      case 'button':
        return { as: rest.as, onClick: rest.onClick, type: 'button' }
      default:
        return { as: rest.as, onClick: rest.onClick, to: rest.to }
    }
  }

  return (
    <li
      {...excludePolymorphicProps(rest)}
      aria-current={!!active}
      className='pb-3 group-last:last:pb-0'
    >
      <StepLink
        {...getLinkProps()}
        active={active}
        available={available}
        complete={complete}
      >
        {name}
      </StepLink>
    </li>
  )
}

export function StepNav ({ children, ...props }: StepNavProps) {
  return (
    <nav {...props}>
      <ul aria-label='Steps'>{children}</ul>
    </nav>
  )
}

StepNav.Step = Step
StepNav.SubStep = SubStep
